import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";

import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import api from "../../../services/api";

// Abas de dados do cadastro do embarcador (PO1-2728; saíram do
// EmbarcadorDetalheModal na sprint 638 para servir também à página do cadastro).
// Tudo em AUTOPILOT_COLORS.

const formatDoc = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return v || "-";
};

const formatCep = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  return d.length === 8 ? d.replace(/(\d{5})(\d{3})/, "$1-$2") : v || "-";
};

const campoLabelStyle = {
  fontSize: "0.72rem",
  color: AUTOPILOT_COLORS.muted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "2px",
};

const campoValueStyle = { fontSize: "0.88rem", color: AUTOPILOT_COLORS.title };

const sectionTitleStyle = {
  fontSize: "0.72rem",
  color: AUTOPILOT_COLORS.accent,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontWeight: 600,
  marginBottom: "0.75rem",
  marginTop: "0.25rem",
  borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
  paddingBottom: "4px",
};

const mutedTextStyle = { color: AUTOPILOT_COLORS.muted, padding: "1rem 0" };

const panelStyle = {
  background: AUTOPILOT_COLORS.insetSurface,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: 8,
  padding: "0.6rem 0.9rem",
};

const statusBoxStyle = (tone) => ({
  background: tone === "success" ? AUTOPILOT_COLORS.statusSuccessBg : tone === "warning" ? AUTOPILOT_COLORS.statusWarningBg : AUTOPILOT_COLORS.statusDangerBg,
  border: `1px solid ${
    tone === "success"
      ? AUTOPILOT_COLORS.statusSuccessBorder
      : tone === "warning"
      ? AUTOPILOT_COLORS.statusWarningBorder
      : AUTOPILOT_COLORS.statusDangerBorder
  }`,
  borderRadius: 6,
  padding: "0.75rem 1rem",
  marginBottom: "1rem",
});

const toneTextColor = (tone) =>
  tone === "success"
    ? AUTOPILOT_COLORS.statusSuccess
    : tone === "warning"
    ? AUTOPILOT_COLORS.statusWarning
    : AUTOPILOT_COLORS.statusDanger;

const Campo = ({ label, value }) => (
  <div style={{ marginBottom: "0.75rem" }}>
    <div style={campoLabelStyle}>{label}</div>
    <div style={campoValueStyle}>{value || "-"}</div>
  </div>
);

const sectionTitle = (text) => <div style={sectionTitleStyle}>{text}</div>;

// ─── Tab: Dados Gerais ────────────────────────────────────────────────────────
const TabDados = ({ dados }) => {
  if (!dados) return <div style={mutedTextStyle}>Carregando...</div>;
  return (
    <div>
      {sectionTitle("Identificação")}
      <div style={{ ...panelStyle, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1.5rem", marginBottom: "1.25rem" }}>
        <Campo label="CNPJ/CPF" value={formatDoc(dados.cnpjCpf)} />
        <Campo label="Status" value={dados.status ? "Ativo" : "Inativo"} />
        <Campo label="Razão Social" value={dados.razaoSocial} />
        <Campo label="Fantasia" value={dados.fantasia} />
        <Campo label="Inscrição Estadual" value={dados.inscricaoEstadual} />
        <Campo label="Inscrição Municipal" value={dados.inscricaoMunicipal} />
        {dados.codSuframa && <Campo label="SUFRAMA" value={dados.codSuframa} />}
        {dados.observacao && <Campo label="Observação" value={dados.observacao} />}
      </div>

      {sectionTitle("Endereço")}
      <div style={{ ...panelStyle, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1.5rem" }}>
        <Campo label="Logradouro" value={dados.logradouro} />
        <Campo label="Número" value={dados.numero} />
        <Campo label="Complemento" value={dados.complemento} />
        <Campo label="Bairro" value={dados.bairro} />
        <Campo label="CEP" value={formatCep(dados.cep)} />
        <Campo label="Cidade / UF" value={dados.cidade ? `${dados.cidade} / ${dados.uf}` : null} />
      </div>
    </div>
  );
};

// ─── Token por origem (dentro da aba Token) ────────────────────────────────────
// Era a aba "Filiais": mesmas linhas de EmbarcadorRemetente que a aba Origens / CDs
// mostra; aqui fica só o que importa para o token, se cada origem já tem o dele.
const TokenPorOrigem = ({ filiais }) => {
  if (!filiais || !filiais.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1.25rem" }}>
      {sectionTitle("Token por origem / CD")}
      {filiais.map((f) => {
        const tone = f.temToken ? "success" : "warning";
        return (
          <div
            key={f.id}
            style={{ ...panelStyle, display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <div>
              <div style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.title }}>{f.fantasia || f.razaoSocial}</div>
              <div style={{ fontSize: "0.75rem", color: AUTOPILOT_COLORS.muted }}>{formatDoc(f.cnpjCpf)}</div>
            </div>
            <div
              style={{
                fontSize: "0.72rem",
                padding: "2px 8px",
                borderRadius: 10,
                background: f.temToken ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.statusWarningBg,
                color: toneTextColor(tone),
                border: `1px solid ${
                  f.temToken ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusWarningBorder
                }`,
              }}
            >
              {f.temToken ? "Com token" : "Sem token"}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Tab: Token ───────────────────────────────────────────────────────────────
const TabToken = ({ embarcadorId, filiais }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAcao, setLoadingAcao] = useState(false);
  const [tokenGerado, setTokenGerado] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);
      const res = await api.get(`/embarcadores/${embarcadorId}/token`);
      setStatus(res.data);
    } catch {
      setErro("Erro ao carregar status do token.");
    } finally {
      setLoading(false);
    }
  }, [embarcadorId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {}
  };

  const gerar = async () => {
    try {
      setLoadingAcao(true);
      setErro(null);
      setConfirmando(false);
      const res = await api.post(`/embarcadores/${embarcadorId}/token`);
      setTokenGerado(res.data.token);
      await carregar();
    } catch (e) {
      setErro(e?.response?.data?.error || "Erro ao gerar token.");
    } finally {
      setLoadingAcao(false);
    }
  };

  const sincronizar = async () => {
    try {
      setLoadingAcao(true);
      setErro(null);
      await api.post(`/embarcadores/${embarcadorId}/token/sincronizar`);
      await carregar();
    } catch (e) {
      setErro(e?.response?.data?.error || "Erro ao sincronizar.");
    } finally {
      setLoadingAcao(false);
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
      </div>
    );

  return (
    <div>
      {erro && (
        <div style={{ ...statusBoxStyle("danger"), color: toneTextColor("danger"), fontSize: "0.82rem" }}>{erro}</div>
      )}

      {tokenGerado && (
        <div style={statusBoxStyle("success")}>
          <div style={{ fontSize: "0.78rem", color: toneTextColor("success"), marginBottom: "0.5rem", fontWeight: 600 }}>
            Token gerado! Guarde-o agora — não será exibido novamente.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <code style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.title, wordBreak: "break-all", flex: 1 }}>
              {tokenGerado}
            </code>
            <Button
              size="sm"
              color={copiado ? "success" : "secondary"}
              onClick={() => copiar(tokenGerado)}
              style={{ whiteSpace: "nowrap", minWidth: "70px" }}
            >
              {copiado ? "Copiado!" : "Copiar"}
            </Button>
          </div>
        </div>
      )}

      {status && (
        <div style={{ ...panelStyle, padding: "1rem 1.25rem", marginBottom: "1rem" }}>
          {status.temToken ? (
            <>
              <div style={{ fontSize: "0.82rem", color: AUTOPILOT_COLORS.text, letterSpacing: "0.05em", marginBottom: "0.4rem" }}>
                {status.tokenMascarado}
              </div>
              <div style={{ fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted }}>
                {status.comToken} origem(ns) com token
                {status.semToken > 0 && (
                  <span style={{ color: AUTOPILOT_COLORS.statusWarning, marginLeft: "0.5rem" }}>
                    · {status.semToken} sem token
                  </span>
                )}
              </div>
            </>
          ) : (
            <div style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.text }}>Nenhum token configurado.</div>
          )}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {!confirmando ? (
          <Button
            color={status?.temToken ? "warning" : "success"}
            onClick={() => (status?.temToken ? setConfirmando(true) : gerar())}
            disabled={loadingAcao}
          >
            {loadingAcao ? <Spinner size="sm" /> : status?.temToken ? "Regenerar Token" : "Gerar Token"}
          </Button>
        ) : (
          <div style={statusBoxStyle("warning")}>
            <div style={{ fontSize: "0.82rem", color: toneTextColor("warning"), marginBottom: "0.6rem" }}>
              O token atual será invalidado imediatamente em todas as origens / CDs. Continuar?
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button color="warning" size="sm" onClick={gerar} disabled={loadingAcao}>
                {loadingAcao ? <Spinner size="sm" /> : "Confirmar"}
              </Button>
              <Button color="secondary" size="sm" onClick={() => setConfirmando(false)} disabled={loadingAcao}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
        {status?.semToken > 0 && (
          <Button color="info" outline onClick={sincronizar} disabled={loadingAcao}>
            {loadingAcao ? <Spinner size="sm" /> : `Sincronizar para ${status.semToken} origem(ns) sem token`}
          </Button>
        )}
      </div>
      <TokenPorOrigem filiais={filiais} />
    </div>
  );
};

export { TabDados, TabToken };
